import {Decadence} from "../Decadence";
import {DecadenceGameAssets} from "../DecadenceGameAssets";
import {gsap} from "gsap"
import {BitmapText} from "pixi.js";
import {DecadenceStatics} from "../config/DecadenceStatics";
import {SpineGSAPWrapper} from "../animation/SpineGSAPWrapper";

export class DecadenceSymbol extends PIXI.Container {

    public reelID:number;
    public reelPosition:number;

    private mainView:PIXI.Container;
    private spinView:PIXI.Container;

    private spine:PIXI.spine.Spine;

    public get isScatterSymbol():boolean {
        return this.name.includes("SC");
    }

    constructor(name:string, reelID:number, reelPos:number) {
        super();
        this.name = name;
        this.reelID = reelID;
        this.reelPosition = reelPos;

        if(!name.includes("SC")) {
            this.initAsText(name);
        } else {
           this.initAsScatter();
        }

        this.addChild(this.mainView);
        this.addChild(this.spinView);
    }

    updateTextWidth() {
        if(this.name == "SC") return;
        const multiText = <PIXI.BitmapText>this.mainView.children[0];
        const multiTextSpin = <PIXI.BitmapText>this.spinView.children[0];
        const newScaleX = DecadenceStatics.SYMBOL_WIDTH / multiText.width;
        const newScaleY = DecadenceStatics.SYMBOL_HEIGHT / multiText.height;
        const newScale = Math.min(newScaleX, newScaleY);
        multiText.scale.set(newScale, newScale);
        multiTextSpin.scale.set(newScale, newScale);
    }

    private initAsText(sym:string) {
        this.name = sym;
        if(sym == "0") sym = "-";
        else sym += "x";
        this.mainView = new PIXI.Container;
        this.mainView.name = "MainView";
        const multiText = new PIXI.BitmapText(sym, {fontName: DecadenceGameAssets.BITMAP_FONT_SYMBOL});
        (<any>multiText.anchor).set(0.5, 0.5);
        this.mainView.addChild(multiText);
        this.addChild(this.mainView);

        this.spinView = new PIXI.Container;
        this.spinView.name = "SpinView";
        const multiTextSpin = new BitmapText(sym, {fontName: DecadenceGameAssets.BITMAP_FONT_SYMBOL_SPIN});
        (<any>multiTextSpin.anchor).set(0.5, 0.5);
        this.spinView.alpha = 0;
        this.spinView.addChild(multiTextSpin);
        this.addChild(this.spinView);

        if(!this.name.includes("SC")) {
            const baseScale = DecadenceStatics.SYMBOLS_BASE_SCALE[this.name];
            this.mainView.scale.set(baseScale, baseScale);
            this.spinView.scale.set(baseScale, baseScale);
        }

        this.updateTextWidth();

        // Move up the dash text
        if(sym == "-") {
            this.mainView.position.y = -7;
            this.spinView.position.y = -7;
        }
    }
    private initAsScatter() {
        const sym = Decadence.model.isFreeSpins ? "SSC" : "SC";

        this.mainView = new PIXI.Container;
        this.mainView.name = "MainView";
        const mainSprite = new PIXI.Sprite(Decadence.assetLoader.getTextures(DecadenceGameAssets.getSymbolTexturePath(sym, false))[0]);
        mainSprite.anchor.set(0.5, 0.5);
        this.mainView.addChild(mainSprite);
        this.addChild(this.mainView);

        this.spine = DecadenceGameAssets.createSpine(DecadenceGameAssets.SCATTER_ANIMATION);
        this.spine.alpha = 0;
        this.mainView.addChild(this.spine);

        this.spinView = new PIXI.Container;
        this.spinView.name = "SpinView";
        const spinSprite = new PIXI.Sprite(Decadence.assetLoader.getTextures(DecadenceGameAssets.getSymbolTexturePath(sym, true))[0]);
        spinSprite.anchor.set(0.5, 0.5);
        this.spinView.alpha = 0;
        this.spinView.addChild(spinSprite);
        this.addChild(this.spinView);

        this.name = sym;
    }
    public reInitialize() {
        this.mainView.removeChildren(0, this.mainView.children.length);
        this.spinView.removeChildren(0, this.spinView.children.length);
        if(this.isScatterSymbol) {
            this.initAsScatter();
        } else {
            this.initAsText(this.name);
        }
    }

    public playLandAnimation():gsap.core.Timeline {
        return gsap.timeline();
    }

    public setSpinning() {
        this.mainView.alpha = 0;
        this.spinView.alpha = 1;
        return this;
    }

    public preCopyHighlight(fadeInDur:number=0.2):gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(gsap.to(this.mainView.scale, {x: 1.2, y: 1.2, ease: "back.out(2)", duration: 0.3}));
        return tl;
    }
    public hidePreCopyHighlight(fadeOutDuration:number=0.1):gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(() => {
            this.mainView.filters = [];
        })
        return tl;
    }

    public animateScale(scaleFactor:number = 1.2, duration:number = 0.3):gsap.core.Timeline {
        return gsap.timeline()
            .add(gsap.to(this.scale, {x: scaleFactor, y: scaleFactor, duration, ease: "back.out(2)"}))
    }

    public changeSymbol(newSym:string) :gsap.core.Timeline {
        return gsap.timeline()
            .add(() => {
                this.removeChild(this.mainView, this.spinView);
                if(newSym == "SC" || newSym == "SSC") {
                    this.initAsScatter();
                } else {
                    this.initAsText(newSym);
                }
                this.name = newSym;
            })
    }

    public getLandAnimation():gsap.core.Timeline {
        if(this.isScatterSymbol) {
            const tl = gsap.timeline();
            tl.add(() => {
                this.spine.alpha = 1;
                const skin = this.name;
                this.spine.skeleton.setSkinByName(skin);
            })
            tl.add(SpineGSAPWrapper.createTimeline(this.spine, 0, "landing"));
            tl.add(() => {
                this.spine.alpha = 0;
            })
            return tl;
        } else {
            return gsap.timeline();
        }
    }
    public playScatterActivate():gsap.core.Timeline {
        if(!this.name.includes("SC")) {return gsap.timeline();}

        const skin = this.name;
        const animation = Decadence.model.isFreeSpins ? "activate_super" : "activate";
        const tl = gsap.timeline();
        tl.add(() => {
            this.spine.alpha = 1;
            this.spine.skeleton.setSkinByName(skin);
        })
        tl.add(SpineGSAPWrapper.createTimeline(this.spine, 0, animation))
        tl.add(() => {
            this.spine.alpha = 0;
        })
        return tl;
    }

    public crossFadeToSpinning(fadeDur:number):gsap.core.Timeline {
        return gsap.timeline()
            .add(gsap.to(this.spinView, {alpha: 1, duration: fadeDur}))
            .add(gsap.to(this.mainView, {alpha: 0, duration: fadeDur}), "<");
    }
    public crossFadeFromSpinning(fadeDur:number):gsap.core.Timeline {
        return gsap.timeline()
            .add(gsap.to(this.spinView, {alpha: 0, duration: fadeDur}))
            .add(gsap.to(this.mainView, {alpha: 1, duration: fadeDur}), "<");
    }
}